import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  SecurityContext,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';

import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
  withLatestFrom,
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { AppState, CountryCode } from '@fcom/core';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  ButtonType,
  IconPosition,
  InputType,
  LoaderTheme,
  NotificationTheme,
} from '@fcom/ui-components';
import { LoginGender, LoginStep } from '@fcom/core-api/login/interfaces/login.interface';
import {
  EMAIL_PATTERN,
  isTruthy,
  isPresent,
  NAME_PATTERN,
  PASSWORD_PATTERN,
  PHONE_PATTERN,
  rangeBetween,
  rangeFrom,
  sortBy,
  uniqueBy,
} from '@fcom/core/utils/utils';
import { LocalDate, TzDate } from '@fcom/core/utils/tz-date';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { finShare } from '@fcom/rx';
import * as LoginActions from '@fcom/core/actions/login.actions';
import { LanguageService } from '@fcom/ui-translate';
import { WindowRef } from '@fcom/core/providers/window';
import { StepperStep } from '@fcom/ui-components/components/stepper/interfaces';

import { ConsentService } from '../../services/consent.service';
import { JoinService } from '../../services/join.service';
import {
  Consent,
  ConsentStatus,
  ConsentText,
  ConsentTextId,
  ConsentTextsGroup,
} from '../../../interfaces/consents.interface';
import { ElementActions, ElementTypes, GaContext } from '../../../interfaces/gtm.interface';
import { SelectOption } from '../../../interfaces/form-interface';
import { finnairPlusNumberValidator } from '../../../utils/form-validators';
import { GtmService } from '../../../gtm/services/gtm.service';
import { MarketingConsentTrackingService } from '../../../gtm/services/marketing-consent-tracking.service';
import { JoinFinnairPlusRequestData, JoinFinnairPlusResponseData } from '../../interfaces/join-finnair-plus.interface';
import {
  FPLUS_JOIN_CONSENT_CONFIG,
  FPLUS_JOIN_CONSENT_DISPLAY_ORDER,
  ConsentDisplayBehavior,
  MEMBER_OFFERS_CONSENTS,
} from '../../constants/join-finnair-plus-consents.config';

export enum JoinFlowStep {
  BASIC_INFO = 'basic',
  CONTACT_DETAILS = 'contact',
  GUARDIAN_DETAILS = 'guardian',
  MARKETING_CONSENT = 'marketing',
  EMAIL_VERIFICATION = 'verification',
  JOIN_COMPLETE = 'completed',
}

const stepToPreviousStep = {
  [JoinFlowStep.BASIC_INFO]: JoinFlowStep.BASIC_INFO,
  [JoinFlowStep.CONTACT_DETAILS]: JoinFlowStep.BASIC_INFO,
  [JoinFlowStep.GUARDIAN_DETAILS]: JoinFlowStep.CONTACT_DETAILS,
  [JoinFlowStep.MARKETING_CONSENT]: JoinFlowStep.CONTACT_DETAILS,
  [JoinFlowStep.EMAIL_VERIFICATION]: JoinFlowStep.EMAIL_VERIFICATION,
};

export enum ApiStatus {
  INITIAL = 'initial',
  JOIN_SUCCESS = 'join_success',
  JOIN_ERROR = 'join_error',
  RESEND_SUCCESS = 'resend_success',
  RESEND_ERROR = 'resend_error',
}

export interface Step extends StepperStep {
  step: JoinFlowStep;
}

@Component({
  selector: 'fin-join-form',
  templateUrl: './join-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class JoinFormComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private store$ = inject<Store<AppState>>(Store);
  private joinService = inject(JoinService);
  private languageService = inject(LanguageService);
  private consentService = inject(ConsentService);
  private router = inject(Router);
  private windowRef = inject(WindowRef);
  private gtmService = inject(GtmService);
  private sanitizer = inject(DomSanitizer);
  private marketingConsentTracking = inject(MarketingConsentTrackingService);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly IconPosition = IconPosition;
  readonly LoaderTheme = LoaderTheme;
  readonly NotificationTheme = NotificationTheme;
  readonly LoginGender = LoginGender;
  readonly JoinFlowStep = JoinFlowStep;
  readonly InputType = InputType;
  readonly ConsentTextId = ConsentTextId;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ApiStatus = ApiStatus;
  readonly MEMBER_OFFERS_CHECKBOX_NAME = 'memberOffers';

  @Input()
  fullScreen = false;

  basicInfoForm: UntypedFormGroup;
  contactDetailsForm: UntypedFormGroup;
  marketingConsentForm: UntypedFormGroup;
  guardianInfoForm: UntypedFormGroup;

  /**
   * Snapshot of the latest consent texts loaded from the consent registry.
   * Used to build the marketing-consent GA event payload at submit time.
   * Captured here so we don't have to subscribe to the shared observable a
   * second time inside `complete()`.
   */
  private latestConsentTexts: ConsentText[] = [];

  stepperSteps$: Observable<Step[]>;
  stepperIndex$: Observable<number>;
  stepTranslations$: Observable<{ title: string; description: string }>;

  consentTextsGroup$: Observable<ConsentTextsGroup>;
  filteredDisplayConsents$: Observable<ConsentText[]>;

  showError$: Observable<boolean>;
  currentStep$: BehaviorSubject<JoinFlowStep> = new BehaviorSubject<JoinFlowStep>(JoinFlowStep.BASIC_INFO);
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  countryOptions: Observable<SelectOption[]>;

  phonePrefixes$: Observable<SelectOption[]>;
  availableDays$: Observable<SelectOption[]>;
  availableMonths$: Observable<SelectOption[]> = of(
    rangeFrom(1, 12).map((month) => ({ name: String(month), value: String(month) }))
  );
  availableYears$: Observable<SelectOption[]> = of(
    rangeBetween(LocalDate.now().minusYears(100).localYear, LocalDate.now().localYear)
      .reverse()
      .map((year) => ({ name: String(year), value: String(year) }))
  );
  isJunior$: Observable<boolean>;
  dateOfBirth$: Observable<LocalDate>;

  memberNumber: string;

  apiStatus$: BehaviorSubject<ApiStatus> = new BehaviorSubject<ApiStatus>(ApiStatus.INITIAL);

  verificationEmailSentText$: Observable<string>;

  email$: ReplaySubject<string> = new ReplaySubject(1);

  private resend$ = new Subject();
  resendLimitReached$: Observable<boolean>;

  get basicInfoFormBirthDate(): UntypedFormGroup {
    return this.basicInfoForm.get('birthDate') as UntypedFormGroup;
  }
  get contactDetailsFormPassword(): UntypedFormGroup {
    return this.contactDetailsForm.get('password') as UntypedFormGroup;
  }
  get contactDetailsFormPhone(): UntypedFormGroup {
    return this.contactDetailsForm.get('phone') as UntypedFormGroup;
  }
  get contactDetailsFormAccept(): UntypedFormControl {
    return this.contactDetailsForm.get('accept') as UntypedFormControl;
  }
  get guardianInfoFormPhone(): UntypedFormGroup {
    return this.guardianInfoForm.get('guardianPhoneNumber') as UntypedFormGroup;
  }

  private subscriptions = new Subscription();

  ngOnInit(): void {
    const countryCodes$: Observable<CountryCode[]> = this.languageService
      .translate<CountryCode[]>('countryCodes')
      .pipe(finShare());
    this.countryOptions = countryCodes$.pipe(
      map((countryCodes) => countryCodes.map(({ name, countryCode }) => ({ name, value: countryCode })))
    );

    this.basicInfoForm = this.fb.group({
      firstName: this.fb.control(
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(56),
          Validators.pattern(NAME_PATTERN),
        ])
      ),
      lastName: this.fb.control(
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(56),
          Validators.pattern(NAME_PATTERN),
        ])
      ),
      birthDate: this.fb.group(
        {
          dateOfBirth: ['', Validators.required],
          monthOfBirth: ['', Validators.required],
          yearOfBirth: ['', Validators.required],
        },
        { validators: [this.ageValidator()] }
      ),
      gender: ['', Validators.required],
    });

    this.contactDetailsForm = this.fb.group({
      phone: this.fb.group({
        phonePrefix: this.fb.control('', Validators.required),
        phoneNumber: this.fb.control('', Validators.compose([Validators.required, Validators.pattern(PHONE_PATTERN)])),
      }),
      email: this.fb.control('', Validators.compose([Validators.required, Validators.pattern(EMAIL_PATTERN)])),
      countryCode: this.fb.control('', Validators.required),
      password: this.fb.group(
        {
          pwd: this.fb.control('', Validators.compose([Validators.required, Validators.pattern(PASSWORD_PATTERN)])),
          confirmPwd: this.fb.control('', Validators.compose([Validators.required])),
        },
        { validators: [this.passwordMatchValidator()] }
      ),
      accept: this.fb.control(false),
    });

    this.guardianInfoForm = this.fb.group({
      guardianName: this.fb.control(
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(56),
          Validators.pattern(NAME_PATTERN),
        ])
      ),
      guardianEmail: this.fb.control('', Validators.compose([Validators.required, Validators.pattern(EMAIL_PATTERN)])),
      guardianPhoneNumber: this.fb.group({
        phonePrefix: this.fb.control('', Validators.required),
        phoneNumber: this.fb.control('', Validators.compose([Validators.required, Validators.pattern(PHONE_PATTERN)])),
      }),
      guardianMemberNumber: this.fb.control('', Validators.compose([finnairPlusNumberValidator()])),
      consent: this.fb.control(false, Validators.requiredTrue),
    });

    this.marketingConsentForm = this.fb.group({});

    this.consentTextsGroup$ = this.consentService.getMarketingConsentTexts().pipe(filter(isPresent), finShare());

    this.filteredDisplayConsents$ = this.consentTextsGroup$.pipe(
      map(({ consentTexts }) => this.filterAndSortConsents(consentTexts))
    );

    this.subscriptions.add(
      this.consentTextsGroup$.subscribe(({ consentTexts }) => {
        this.latestConsentTexts = consentTexts;
        this.createMarketingConsentForm(consentTexts);
      })
    );

    this.availableDays$ = this.getDays(
      this.basicInfoForm.get('birthDate.yearOfBirth'),
      this.basicInfoForm.get('birthDate.monthOfBirth')
    ).pipe(map((days) => days.map((day) => ({ name: String(day), value: String(day) }))));

    this.dateOfBirth$ = combineLatest([
      this.basicInfoForm.get('birthDate.yearOfBirth').valueChanges,
      this.basicInfoForm.get('birthDate.monthOfBirth').valueChanges,
      this.basicInfoForm.get('birthDate.dateOfBirth').valueChanges,
    ]).pipe(
      map(([year, month, day]: [string, string, string]) => {
        if (year && month && day) {
          return LocalDate.forDate(year, month, day);
        }
        return undefined;
      }),
      finShare()
    );

    this.isJunior$ = this.dateOfBirth$.pipe(
      map((day) => {
        return day && day.gte(LocalDate.now().minusYears(18).plusDays(1));
      }),
      startWith(false),
      finShare()
    );

    this.subscriptions.add(
      this.isJunior$.subscribe((isJunior) => {
        this.contactDetailsFormAccept.setValidators(isJunior ? null : Validators.requiredTrue);
        this.contactDetailsFormAccept.reset(false);
      })
    );

    this.phonePrefixes$ = countryCodes$.pipe(
      take(1),
      tap((countryCodes: CountryCode[]) => {
        const phonePrefix =
          countryCodes.find(({ countryCode }) => countryCode === this.languageService.ddsLocaleValue.countrySite)
            ?.phonePrefix ?? '';
        this.contactDetailsForm.get('phone').get('phonePrefix').setValue(phonePrefix);
        this.guardianInfoForm.get('guardianPhoneNumber').get('phonePrefix').setValue(phonePrefix);
      }),
      map((countryCodes: CountryCode[]) =>
        sortBy(
          countryCodes.filter(uniqueBy(({ phonePrefix }) => phonePrefix)).map((cc) => ({
            name: `(+${cc.phonePrefix})`,
            value: cc.phonePrefix,
          })),
          (option) => option.name
        )
      ),
      finShare()
    );
    this.stepTranslations$ = this.currentStep$.pipe(
      withLatestFrom(this.isJunior$),
      map(([step, junior]) => (junior && step === JoinFlowStep.CONTACT_DETAILS ? 'junior' : step)),
      map((prefix) => ({
        title: `login.join.${prefix}Title`,
        description: `login.join.${prefix}Description`,
      })),
      finShare()
    );

    this.stepperSteps$ = this.currentStep$.pipe(
      withLatestFrom(this.isJunior$),
      map(([currentStep, junior]) => {
        return currentStep === JoinFlowStep.JOIN_COMPLETE
          ? []
          : [
              JoinFlowStep.BASIC_INFO,
              JoinFlowStep.CONTACT_DETAILS,
              JoinFlowStep.GUARDIAN_DETAILS,
              JoinFlowStep.MARKETING_CONSENT,
              JoinFlowStep.EMAIL_VERIFICATION,
            ]
              .filter((step) =>
                junior ? step !== JoinFlowStep.MARKETING_CONSENT : step !== JoinFlowStep.GUARDIAN_DETAILS
              )
              .map((step) => ({
                step,
                ariaLabel: `login.join.${step}Aria`,
              }));
      })
    );

    this.stepperIndex$ = this.currentStep$.pipe(
      withLatestFrom(this.stepperSteps$),
      map(([currentStep, steps]) => steps.findIndex(({ step }) => step === currentStep))
    );

    this.subscriptions.add(
      this.router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe(() => this.closeDialog())
    );

    this.verificationEmailSentText$ = this.email$.pipe(
      switchMap((unsanitizedEmail) => {
        const email = this.sanitizer.sanitize(SecurityContext.HTML, unsanitizedEmail);
        return this.languageService.translate('login.join.verificationEmailSent', {
          email,
        });
      })
    );

    const maxResendCount = 2;

    const resendStatus$ = this.resend$.pipe(
      take(maxResendCount),
      switchMap(() => this.joinService.resendAccountVerificationEmail(this.memberNumber))
    );

    this.subscriptions.add(
      resendStatus$.subscribe({
        next: () => {
          return this.apiStatus$.next(ApiStatus.RESEND_SUCCESS);
        },
        error: () => {
          return this.apiStatus$.next(ApiStatus.RESEND_ERROR);
        },
      })
    );

    this.resendLimitReached$ = this.resend$.pipe(
      skip(maxResendCount - 1),
      map((_value) => {
        return true;
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  openLogin(): void {
    this.trackEvent('open-login');
    this.closeDialog();
    this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
  }

  moveToStep(step: JoinFlowStep): void {
    switch (step) {
      case JoinFlowStep.CONTACT_DETAILS:
        if (this.basicInfoForm.invalid) {
          this.basicInfoForm.markAllAsTouched();
          return;
        }
        break;
      case JoinFlowStep.GUARDIAN_DETAILS:
      case JoinFlowStep.MARKETING_CONSENT:
        if (this.contactDetailsForm.invalid) {
          this.contactDetailsForm.markAllAsTouched();
          return;
        }
        break;
    }

    this.trackEvent(`open-step-${step}`);

    if (step === JoinFlowStep.MARKETING_CONSENT) {
      this.trackEvent('set-consent-preferences', undefined, ElementTypes.MODAL, ElementActions.VIEW);
    }

    this.currentStep$.next(step);
  }

  closeDialog(): void {
    this.trackEvent('close-dialog');
    this.joinService.joinDialogOpen$.next(false);
  }

  closeError(): void {
    this.apiStatus$.next(ApiStatus.INITIAL);
  }

  complete(isJunior = false): void {
    if (isJunior && this.guardianInfoForm.invalid) {
      this.guardianInfoForm.markAllAsTouched();
      return;
    }

    this.loading$.next(true);
    this.apiStatus$.next(ApiStatus.INITIAL);

    const data = this.signUpData(isJunior);

    this.subscriptions.add(
      this.joinService.joinFinnairPlus(data).subscribe({
        next: (responseData: JoinFinnairPlusResponseData) => {
          this.memberNumber = responseData.memberNumber;
          this.trackEvent('verify-email', 'account-pending', ElementTypes.MODAL, ElementActions.VIEW);
          this.currentStep$.next(JoinFlowStep.EMAIL_VERIFICATION);
          this.loading$.next(false);
          this.apiStatus$.next(ApiStatus.JOIN_SUCCESS);

          if (!isJunior) {
            this.trackJoinMarketingConsentChange(data.marketingPermissions ?? {});
          }
        },
        error: () => {
          this.trackEvent('join-failed', 'false');
          this.loading$.next(false);
          this.apiStatus$.next(ApiStatus.JOIN_ERROR);
        },
      })
    );
  }

  resend(): void {
    this.resend$.next(null);
  }

  readMore(): void {
    this.trackEvent('read-more');
    this.subscriptions.add(
      this.languageService
        .translate('fragments.plusReadMore.url')
        .subscribe((url) => this.windowRef.nativeWindow.open(url, '_blank'))
    );
  }

  openStep(stepperStep: StepperStep): void {
    if (this.currentStep$.getValue() === JoinFlowStep.EMAIL_VERIFICATION) {
      return;
    } else {
      const { step } = stepperStep as Step;
      this.trackEvent(`open-stepper-step-${step}`);
      this.apiStatus$.next(ApiStatus.INITIAL);
      this.currentStep$.next(step);
    }
  }

  goBack(_event: Event): void {
    const previousStep = stepToPreviousStep[this.currentStep$.getValue()];
    this.apiStatus$.next(ApiStatus.INITIAL);
    this.currentStep$.next(previousStep);
  }

  removeLeadingZero(event: Event): void {
    if (event.type !== 'input') {
      return;
    }

    const inputEvent = event as InputEvent;
    const inputField = event.target as HTMLInputElement;

    if (!inputEvent.inputType && inputField.value[0] === '0') {
      this.contactDetailsFormPhone.get('phoneNumber').setValue(inputField.value.slice(1));
    }
  }

  private getConsentDisplayBehaviour(consent: ConsentText): ConsentDisplayBehavior {
    return FPLUS_JOIN_CONSENT_CONFIG[consent.consentTextId] || ConsentDisplayBehavior.SHOW_UNCHECKED;
  }

  private createMarketingConsentForm(consents: ConsentText[]) {
    const consentFields = consents.reduce((fields, consent) => {
      const behavior = this.getConsentDisplayBehaviour(consent);

      if (behavior === ConsentDisplayBehavior.IGNORE) {
        return fields;
      }

      const defaultValue =
        behavior === ConsentDisplayBehavior.SHOW_CHECKED || behavior === ConsentDisplayBehavior.AUTO_TRUE;

      fields[consent.consentTextId] = this.fb.control(defaultValue, { nonNullable: true });
      return fields;
    }, {});
    this.marketingConsentForm = this.fb.group({
      ...consentFields,
      [this.MEMBER_OFFERS_CHECKBOX_NAME]: this.fb.control(false, { nonNullable: true }),
    });
  }

  private sortConsentsBasedOnPredefinedOrder(a: ConsentText, b: ConsentText): number {
    const orderLength = FPLUS_JOIN_CONSENT_DISPLAY_ORDER.length;
    const indexA = FPLUS_JOIN_CONSENT_DISPLAY_ORDER.indexOf(a.consentTextId);
    const indexB = FPLUS_JOIN_CONSENT_DISPLAY_ORDER.indexOf(b.consentTextId);

    // Items not found in the predefined order (-1) fall to the end of the list
    return (indexA === -1 ? orderLength : indexA) - (indexB === -1 ? orderLength : indexB);
  }

  private filterAndSortConsents(consents: ConsentText[]): ConsentText[] {
    return consents
      .filter((consent) => {
        const behavior = this.getConsentDisplayBehaviour(consent);

        return behavior === ConsentDisplayBehavior.SHOW_CHECKED || behavior === ConsentDisplayBehavior.SHOW_UNCHECKED;
      })
      .sort(this.sortConsentsBasedOnPredefinedOrder)
      .filter((consent) => !MEMBER_OFFERS_CONSENTS.includes(consent.consentTextId));
  }

  private signUpData(isJunior: boolean): JoinFinnairPlusRequestData {
    const { birthDate, firstName, lastName, gender } = this.basicInfoForm.getRawValue();
    const { accept, email, password, phone, countryCode } = this.contactDetailsForm.getRawValue();
    const date = TzDate.utcMidnight(birthDate.yearOfBirth, birthDate.monthOfBirth, birthDate.dateOfBirth);

    const data: JoinFinnairPlusRequestData = {
      firstName,
      lastName,
      gender,
      dateOfBirth: date.toTzString(),
      password: password.pwd,
      phoneNumber: `+${phone.phonePrefix}${phone.phoneNumber}`,
      enrollmentChannel: 'Web',
      locale: this.languageService.localeValue.replace('_', '-'),
      countryCode,
      email,
    };

    if (isJunior) {
      const { consent, guardianEmail, guardianMemberNumber, guardianName, guardianPhoneNumber } =
        this.guardianInfoForm.getRawValue();
      return {
        ...data,
        consent,
        guardianName,
        guardianEmail,
        guardianMemberNumber,
        guardianPhoneNumber: `+${guardianPhoneNumber.phonePrefix}${guardianPhoneNumber.phoneNumber}`,
      };
    }

    const marketingConsentForm = this.marketingConsentForm.getRawValue();

    return {
      ...data,
      agreements: accept,
      marketingPermissions: this.mapMemberOfferConsentValues(marketingConsentForm),
    };
  }

  private mapMemberOfferConsentValues(formValues: Record<string, boolean>): Partial<Record<ConsentTextId, boolean>> {
    const memberOffersConsentValue = formValues[this.MEMBER_OFFERS_CHECKBOX_NAME];

    const mappedValues = {
      ...formValues,
      [ConsentTextId.MEMBER_OFFERS_FINANCIAL]: memberOffersConsentValue,
      [ConsentTextId.MEMBER_OFFERS_TRAVEL]: memberOffersConsentValue,
      [ConsentTextId.MEMBER_OFFERS_RETAIL]: memberOffersConsentValue,
      [ConsentTextId.MEMBER_OFFERS_EVENT]: memberOffersConsentValue,
    };

    delete mappedValues[this.MEMBER_OFFERS_CHECKBOX_NAME];

    return mappedValues;
  }

  private getDays = (yearControl: AbstractControl, monthControl: AbstractControl): Observable<number[]> => {
    return combineLatest([
      yearControl.valueChanges.pipe(startWith(yearControl.value as string), filter(isTruthy)),
      monthControl.valueChanges.pipe(startWith(monthControl.value as string), filter(isTruthy)),
    ]).pipe(
      map(([year, month]: [string, string]) => LocalDate.getAmountOfDaysInMonth(Number(year), Number(month))),
      map((numberOfDays) => rangeFrom(1, numberOfDays)),
      startWith(rangeFrom(1, 31)),
      distinctUntilChanged(),
      finShare()
    );
  };

  private passwordMatchValidator = (): ValidatorFn => {
    return (control: AbstractControl): Record<string, unknown> | null => {
      if (
        !control.get('pwd').pristine &&
        !control.get('confirmPwd').pristine &&
        control.get('pwd').value !== control.get('confirmPwd').value
      ) {
        return { match: true };
      }
      return null;
    };
  };

  private ageValidator = (): ValidatorFn => {
    return (control: AbstractControl): Record<string, unknown> | null => {
      if (control.get('dateOfBirth').value && control.get('monthOfBirth').value && control.get('yearOfBirth').value) {
        const date = LocalDate.forDate(
          control.get('yearOfBirth').value,
          control.get('monthOfBirth').value,
          control.get('dateOfBirth').value
        );
        if (date.isBetween(LocalDate.now().minusYears(2).plusDays(1), LocalDate.now())) {
          return { tooYoung: true };
        }
        if (date.gte(LocalDate.now())) {
          return { invalid: true };
        }
      }
      return null;
    };
  };

  private trackEvent(
    elementName: string,
    state: string = undefined,
    elementType = ElementTypes.BUTTON,
    action = ElementActions.CLICK
  ): void {
    this.gtmService.trackElement(`join-form-${elementName}`, GaContext.FINNAIR_PLUS, elementType, state, action);
  }

  private trackJoinMarketingConsentChange(marketingPermissions: Partial<Record<ConsentTextId, boolean>>): void {
    if (!this.latestConsentTexts.length) {
      return;
    }

    const updatedConsents: Consent[] = this.latestConsentTexts.map((consentText) => ({
      consentTextId: consentText.consentTextId,
      effectiveFrom: consentText.effectiveFrom,
      text: consentText.text,
      textHash: consentText.textHash,
      analyticsCode: consentText.analyticsCode,
      consentStatus: marketingPermissions[consentText.consentTextId] ? ConsentStatus.ACCEPTED : ConsentStatus.DECLINED,
    }));

    const previousConsents: Consent[] = updatedConsents.map((c) => ({
      ...c,
      consentStatus: ConsentStatus.DECLINED,
    }));

    this.marketingConsentTracking.writeConsentCookie(updatedConsents);
    this.marketingConsentTracking.trackConsentChange({
      context: GaContext.FINNAIR_PLUS,
      id: 'join-form-consent-preferences',
      changes: updatedConsents.map((current, index) => ({
        previous: previousConsents[index],
        current,
      })),
    });
  }

  @HostListener('document:keydown.escape') onKeydownHandler(): void {
    this.closeDialog();
  }
}
